/**
 * PageLayout - Standard Page-Wrapper
 *
 * Bietet konsistentes Padding, max-width und optionale Header/Footer-Slots.
 * WICHTIG: Diese Komponente hat KEIN overflow - sie verlässt sich auf
 * den übergeordneten AppLayout.main als Scroll-Container.
 *
 * STICKY HEADER:
 * Der stickyHeader wird NICHT hier gerendert, sondern via Context an
 * AppLayout übergeben und dort als direktes Kind des Scroll-Containers
 * gerendert. Dies ist notwendig, weil CSS sticky nicht in verschachtelten
 * Flex-Containern funktioniert.
 *
 * HINTERGRUND:
 * Der Gradient-Hintergrund ist global auf body definiert (design-variables.css).
 * Seiten müssen sich nicht darum kümmern.
 *
 * @example
 * ```tsx
 * <PageLayout maxWidth="lg" padding="md">
 *   <h1>Page Title</h1>
 *   <p>Page content...</p>
 * </PageLayout>
 *
 * // Mit Header und Footer
 * <PageLayout
 *   header={<PageHeader title="Dashboard" />}
 *   footer={<PageActions />}
 *   maxWidth="xl"
 * >
 *   <DashboardContent />
 * </PageLayout>
 *
 * // Mit Sticky Header (für Listen-Seiten wie Matches, Characters)
 * <PageLayout
 *   stickyHeader={<FilterBar />}
 *   maxWidth="xl"
 * >
 *   <InfiniteList />
 * </PageLayout>
 * ```
 */

import type { ReactNode } from 'react';
import { VStack, HStack, Grid } from '@/components/core';
import { cn } from '@/lib/utils';
import { useStickyHeader } from '@/contexts/StickyHeaderContext';

export interface PageLayoutProps {
  /**
   * Der Hauptinhalt der Seite
   */
  children: ReactNode;

  /**
   * Optionaler Header-Bereich (z.B. Page-Titel, Breadcrumbs)
   */
  header?: ReactNode;

  /**
   * Optionaler Sticky-Header-Bereich (z.B. Filter, Tabs für Listen-Seiten)
   * Dieser Header bleibt beim Scrollen am oberen Bildschirmrand fixiert
   * und hat einen Glaseffekt wie im ChatLayout.
   */
  stickyHeader?: ReactNode;

  /**
   * Optionaler Footer-Bereich (z.B. Aktions-Buttons)
   */
  footer?: ReactNode;

  /**
   * Maximale Breite des Inhalts
   * @default 'xl'
   */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

  /**
   * Padding um den Inhalt
   * @default 'md'
   */
  padding?: 'none' | 'sm' | 'md' | 'lg';

  /**
   * Zusaetzliche CSS-Klasse fuer den Container
   */
  className?: string;

  /**
   * Ob der Content zentriert werden soll
   * @default true
   */
  centered?: boolean;

  /**
   * Ob der Content den vollen vertikalen Raum einnehmen soll
   * @default false
   */
  fullHeight?: boolean;
}

// Max-Width Mapping zu Tailwind-Klassen
const maxWidthClasses: Record<NonNullable<PageLayoutProps['maxWidth']>, string> = {
  sm: 'max-w-screen-sm',      // 640px
  md: 'max-w-screen-md',      // 768px
  lg: 'max-w-screen-lg',      // 1024px
  xl: 'max-w-screen-xl',      // 1280px
  '2xl': 'max-w-screen-2xl',  // 1536px
  full: 'max-w-full',
};

// Padding Mapping zu Tailwind-Klassen
const paddingClasses: Record<NonNullable<PageLayoutProps['padding']>, string> = {
  none: 'p-0',
  sm: 'px-token-xs py-token-sm sm:px-token-md sm:py-token-md',
  md: 'px-token-md py-token-md sm:px-token-lg sm:py-token-lg',
  lg: 'px-token-md py-token-lg sm:px-token-xl sm:py-token-xl',
};

// NOTE: horizontalPaddingClasses wurde entfernt - sticky header wird jetzt
// in AppLayout gerendert mit eigenem Padding

export function PageLayout({
  children,
  header,
  stickyHeader,
  footer,
  maxWidth = 'xl',
  padding = 'md',
  className,
  centered = true,
  fullHeight = false,
}: PageLayoutProps) {
  // Sticky Header wird an AppLayout übergeben (via Context)
  // und dort als direktes Kind des Scroll-Containers gerendert
  useStickyHeader(stickyHeader ?? null);

  const Wrapper = fullHeight ? VStack : 'div';

  return (
    <Wrapper
      className={cn(
        // Basis-Layout
        'page-layout w-full',
        // Full height option
        fullHeight && 'min-h-full',
        // WICHTIG: Kein overflow hier - parent scrollt
        'overflow-visible',
        className
      )}
    >
      {/* Optional: Page Header (non-sticky) */}
      {header && (
        <div
          className={cn(
            'page-layout-header w-full',
            centered && 'mx-auto',
            maxWidthClasses[maxWidth],
            paddingClasses[padding],
            // Header hat nur horizontales Padding, kein vertikales doppelt
            'py-0 pt-token-md sm:pt-token-lg'
          )}
        >
          {header}
        </div>
      )}

      {/* HINWEIS: Sticky Header wird NICHT hier gerendert!
          Er wird via useStickyHeader an AppLayout übergeben und dort
          als direktes Kind des Scroll-Containers gerendert.
          Dies ist notwendig für korrektes CSS sticky-Verhalten. */}

      {/* Main Content */}
      <div
        className={cn(
          'page-layout-content w-full flex-1',
          centered && 'mx-auto',
          maxWidthClasses[maxWidth],
          paddingClasses[padding],
          // Wenn stickyHeader vorhanden, reduziertes top-padding
          // (der sticky header in AppLayout hat bereits Abstand)
          stickyHeader && 'pt-token-xs sm:pt-token-sm'
        )}
      >
        {children}
      </div>

      {/* Optional: Page Footer */}
      {footer && (
        <div
          className={cn(
            'page-layout-footer w-full',
            centered && 'mx-auto',
            maxWidthClasses[maxWidth],
            paddingClasses[padding],
            // Footer hat nur horizontales Padding
            'py-0 pb-token-md sm:pb-token-lg'
          )}
        >
          {footer}
        </div>
      )}
    </Wrapper>
  );
}

export default PageLayout;
